import pagedownButtons from '../data/pagedownButtons';

let buttonCount = 2; // 2 for undo/redo
let spacerCount = 0;
pagedownButtons.forEach((button) => {
  if (button.method) {
    buttonCount += 1;
  } else {
    spacerCount += 1;
  }
});

const minPadding = 25;
const editorTopPadding = 10;
const navigationBarEditButtonsWidth = (34 * buttonCount) + (8 * spacerCount); // buttons + spacers
const navigationBarLeftButtonWidth = 38 + 4 + 12; // Explorer button only
const navigationBarRightButtonWidth = 38 + 8 + 44; // Menu + TOC buttons
const navigationBarSpinnerWidth = 24 + 8 + 5; // 5 for left margin
const navigationBarLocationWidth = 20;
const navigationBarSyncPublishButtonsWidth = 34 + 10;
const navigationBarTitleMargin = 8;
const maxTitleMaxWidth = 9999; // Allow title to use full available width
const minTitleMaxWidth = 200;

const constants = {
  editorMinWidth: 320,
  explorerWidth: 260,
  gutterWidth: 250,
  sideBarWidth: 280,
  navigationBarHeight: 44,
  buttonBarWidth: 36,
  statusBarHeight: 20,
};

function computeStyles(state, getters, layoutSettings = getters['data/layoutSettings'], styles = {
  showNavigationBar: layoutSettings.showNavigationBar
    || !layoutSettings.showEditor
    || state.content.revisionContent
    || state.light,
  showStatusBar: layoutSettings.showStatusBar,
  showEditor: layoutSettings.showEditor,
  showSidePreview: layoutSettings.showSidePreview && layoutSettings.showEditor,
  showPreview: layoutSettings.showSidePreview || !layoutSettings.showEditor,
  showSideBar: layoutSettings.showSideBar && !state.light,
  showExplorer: layoutSettings.showExplorer && !state.light,
  layoutOverflow: false,
  hideLocations: state.light,
}) {
  styles.innerHeight = state.layout.bodyHeight;
  if (styles.showNavigationBar) {
    styles.innerHeight -= constants.navigationBarHeight;
  }
  if (styles.showStatusBar) {
    styles.innerHeight -= constants.statusBarHeight;
  }

  styles.innerWidth = state.layout.bodyWidth;
  if (styles.innerWidth < constants.editorMinWidth
    + constants.gutterWidth + constants.buttonBarWidth
  ) {
    styles.layoutOverflow = true;
  }
  // Only subtract sidebar/explorer width when they're not overlays (> 900px)
  // At <= 900px, they become absolutely positioned overlays via CSS
  const isOverlayMode = state.layout.bodyWidth <= 900;
  if (styles.showSideBar && !isOverlayMode) {
    styles.innerWidth -= constants.sideBarWidth;
  }
  if (styles.showExplorer && !isOverlayMode) {
    styles.innerWidth -= constants.explorerWidth;
  }

  // At very small screens (<= 360px), button bar becomes overlay, don't subtract its width
  const isVerySmallScreen = state.layout.bodyWidth <= 360;
  const effectiveButtonBarWidth = isVerySmallScreen ? 0 : constants.buttonBarWidth;
  
  let doublePanelWidth = styles.innerWidth - effectiveButtonBarWidth;
  if (doublePanelWidth < constants.editorMinWidth) {
    doublePanelWidth = constants.editorMinWidth;
  }
  
  // Store this for use in styles
  styles.buttonBarOverlay = isVerySmallScreen;

  if (styles.showSidePreview && doublePanelWidth / 2 < constants.editorMinWidth) {
    styles.showSidePreview = false;
    styles.showPreview = false;
    styles.layoutOverflow = false;
    return computeStyles(state, getters, layoutSettings, styles);
  }

  // Hide side preview on mobile (when editor buttons move to bottom toolbar)
  // But keep preview visible if we're in reader mode (showEditor = false)
  if (styles.showSidePreview && state.layout.bodyWidth <= 870) {
    styles.showSidePreview = false;
    // Only hide preview if editor is showing (side-by-side mode)
    // In reader mode, we need to keep showing the preview
    if (styles.showEditor) {
      styles.showPreview = false;
    }
    return computeStyles(state, getters, layoutSettings, styles);
  }

  const computedSettings = getters['data/computedSettings'];
  styles.fontSize = 18;
  styles.textWidth = 990;
  if (doublePanelWidth < 1120) {
    styles.fontSize -= 1;
    styles.textWidth = 910;
  }
  if (doublePanelWidth < 1040) {
    styles.textWidth = 830;
  }
  styles.textWidth *= computedSettings.maxWidthFactor;
  if (doublePanelWidth < styles.textWidth) {
    styles.textWidth = doublePanelWidth;
  }
  if (styles.textWidth < 640) {
    styles.fontSize -= 1;
  }
  styles.fontSize *= computedSettings.fontSizeFactor;

  const bottomPadding = Math.floor(styles.innerHeight / 2);
  const panelWidth = Math.floor(doublePanelWidth / 2);
  styles.previewWidth = styles.showSidePreview ?
    panelWidth :
    doublePanelWidth;
  const previewRightPadding = Math
    .max(Math.floor((styles.previewWidth - styles.textWidth) / 2), minPadding);
  if (!styles.showSidePreview) {
    styles.previewWidth += constants.buttonBarWidth;
  }
  styles.previewGutterWidth = 0;
  const previewLeftPadding = previewRightPadding + styles.previewGutterWidth;
  styles.previewGutterLeft = previewLeftPadding - minPadding;
  styles.previewPadding = `${editorTopPadding}px ${previewRightPadding}px ${bottomPadding}px ${previewLeftPadding}px`;
  styles.editorWidth = styles.showSidePreview ?
    panelWidth :
    doublePanelWidth;
  const editorRightPadding = Math
    .max(Math.floor((styles.editorWidth - styles.textWidth) / 2), minPadding);
  styles.editorGutterWidth = 0;
  const editorLeftPadding = editorRightPadding + styles.editorGutterWidth;
  styles.editorGutterLeft = editorLeftPadding - minPadding;
  styles.editorPadding = `${editorTopPadding}px ${editorRightPadding}px ${bottomPadding}px ${editorLeftPadding}px`;

  // Navigation bar spans full bodyWidth, not innerWidth (which has sidebar/explorer subtracted)
  const navBarWidth = state.layout.bodyWidth;
  
  // On mobile (<=768px), edit buttons are hidden via CSS, so don't subtract their width
  // Also use smaller button widths that match CSS responsive styles
  const isMobile = navBarWidth <= 768;
  const isSmallMobile = navBarWidth <= 480;
  const editButtonsWidth = isMobile ? 0 : navigationBarEditButtonsWidth;
  
  // Button widths are smaller on mobile (matches CSS responsive breakpoints)
  const leftButtonWidth = isSmallMobile ? (34 + 4 + 8) : navigationBarLeftButtonWidth;
  const rightButtonWidth = isSmallMobile ? (34 + 8 + 34) : navigationBarRightButtonWidth;
  const spinnerWidth = isSmallMobile ? (20 + 4 + 5) : navigationBarSpinnerWidth;
  const titleMinWidth = isSmallMobile ? 80 : minTitleMaxWidth;
  
  // Subtract button bar width so title aligns with it
  styles.titleMaxWidth = navBarWidth -
    leftButtonWidth -
    rightButtonWidth -
    spinnerWidth -
    editButtonsWidth -
    navigationBarTitleMargin -
    constants.buttonBarWidth;
  styles.titleMaxWidth = Math
    .max(titleMinWidth, Math
      .min(maxTitleMaxWidth, styles.titleMaxWidth));
  return styles;
}

export default {
  namespaced: true,
  state: {
    canUndo: false,
    canRedo: false,
    bodyWidth: 0,
    bodyHeight: 0,
  },
  mutations: {
    setCanUndo: (state, value) => {
      state.canUndo = value;
    },
    setCanRedo: (state, value) => {
      state.canRedo = value;
    },
    updateBodySize: (state) => {
      state.bodyWidth = document.body.clientWidth;
      state.bodyHeight = document.body.clientHeight;
    },
  },
  getters: {
    constants: () => constants,
    styles: (state, getters, rootState, rootGetters) => computeStyles(rootState, rootGetters),
  },
  actions: {
    updateBodySize({ commit, dispatch, rootGetters }) {
      commit('updateBodySize');
      // Make sure both explorer and side bar are not open if body width is small
      const layoutSettings = rootGetters['data/layoutSettings'];
      dispatch('data/toggleExplorer', layoutSettings.showExplorer, { root: true });
    },
  },
};
